import { PERMISSIONS } from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Badge, Button, ErrorBox, Field, Input, PageHeader, Select, Spinner } from '@/components/ui';
import { ApiError, api } from '@/lib/api';
import { useUser } from '@/lib/auth';
import { formatMoment } from '@/lib/format';
import { departmentsTreeQuery, flattenTree } from '@/lib/queries';

interface AccessUser {
  id: string;
  login: string;
  isActive: boolean;
  lastLoginAt: string | null;
  fullName: string | null;
  employeeId: string | null;
  roles: {
    id: string;
    roleCode: string;
    roleName: string;
    scopeDepartmentId: string | null;
    scopeDepartmentName: string | null;
  }[];
}

interface Role {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isImplicit: boolean;
  permissions: string[];
}

export function AdminAccessPage() {
  const [tab, setTab] = useState<'users' | 'roles'>('users');
  return (
    <div className="max-w-[1100px]">
      <PageHeader title="Учётки и роли" description="Кто может войти в систему и что ему доступно." />
      <div role="tablist" className="mb-5 inline-flex rounded-md border border-line-strong bg-surface p-0.5">
        {(
          [
            ['users', 'Учётки'],
            ['roles', 'Роли и права'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            type="button"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`h-8 rounded px-4 text-[13.5px] ${tab === key ? 'bg-graphite text-white' : 'text-ink-soft hover:bg-sunken'}`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'users' ? <UsersTab /> : <RolesTab />}
    </div>
  );
}

function UsersTab() {
  const qc = useQueryClient();
  const users = useQuery({
    queryKey: ['access', 'users'],
    queryFn: () => api<AccessUser[]>('/access/users'),
  });
  const roles = useQuery({ queryKey: ['access', 'roles'], queryFn: () => api<Role[]>('/access/roles') });
  const tree = useQuery(departmentsTreeQuery);
  const me = useUser();
  const [assignFor, setAssignFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['access'] });

  const toggle = useMutation({
    mutationFn: (u: AccessUser) =>
      api(`/access/users/${u.id}`, { method: 'PATCH', body: { isActive: !u.isActive } }),
    onSuccess: refresh,
    onError: (e) => setError(e.message),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/access/user-roles/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: (e) => setError(e.message),
  });
  const assign = useMutation({
    mutationFn: (v: { userId: string; roleCode: string; scopeDepartmentId: string | null }) =>
      api(`/access/users/${v.userId}/roles`, { method: 'POST', body: v }),
    onSuccess: () => {
      setAssignFor(null);
      refresh();
    },
    onError: (e) => setError(e.message),
  });

  if (users.isPending) return <Spinner />;
  if (users.error) return <ErrorBox error={users.error} />;
  const assignable = roles.data?.filter((r) => !r.isImplicit) ?? [];

  return (
    <div>
      {error && (
        <div className="mb-4">
          <ErrorBox error={new Error(error)} />
        </div>
      )}
      <NewUserForm onCreated={refresh} />
      <div className="panel overflow-x-auto">
        <table className="w-full min-w-[760px] text-left">
          <thead>
            <tr className="border-b border-line text-[13px] text-steel">
              <th scope="col" className="px-4 py-2.5 font-medium">
                Логин
              </th>
              <th scope="col" className="px-4 py-2.5 font-medium">
                Сотрудник
              </th>
              <th scope="col" className="px-4 py-2.5 font-medium">
                Роли
              </th>
              <th scope="col" className="px-4 py-2.5 font-medium">
                Последний вход
              </th>
              <th scope="col" className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {users.data.map((u) => (
              <tr key={u.id} className={u.isActive ? '' : 'text-steel'}>
                <td className="px-4 py-2.5 font-medium">{u.login}</td>
                <td className="px-4 py-2.5">{u.fullName ?? '—'}</td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {u.roles.map((r) => (
                      <span
                        key={r.id}
                        className="inline-flex items-center gap-1 rounded bg-sunken py-0.5 pl-1.5 pr-1 text-[12.5px]"
                      >
                        {r.roleName}
                        {r.scopeDepartmentName && (
                          <span className="text-steel">в отделе «{r.scopeDepartmentName}»</span>
                        )}
                        <button
                          type="button"
                          aria-label={`Снять роль ${r.roleName}`}
                          onClick={() => revoke.mutate(r.id)}
                          className="text-steel hover:text-danger"
                        >
                          <X className="size-3.5" />
                        </button>
                      </span>
                    ))}
                    {assignFor === u.id ? (
                      <form
                        className="flex flex-wrap items-center gap-1.5"
                        onSubmit={(e: FormEvent<HTMLFormElement>) => {
                          e.preventDefault();
                          const fd = new FormData(e.currentTarget);
                          assign.mutate({
                            userId: u.id,
                            roleCode: String(fd.get('roleCode')),
                            scopeDepartmentId: (fd.get('scope') as string) || null,
                          });
                        }}
                      >
                        <Select name="roleCode" aria-label="Роль" className="h-8 w-auto text-[13px]">
                          {assignable.map((r) => (
                            <option key={r.code} value={r.code}>
                              {r.name}
                            </option>
                          ))}
                        </Select>
                        <Select name="scope" aria-label="Область действия" className="h-8 w-auto text-[13px]">
                          <option value="">Вся компания</option>
                          {tree.data &&
                            flattenTree(tree.data).map(({ node, depth }) => (
                              <option key={node.id} value={node.id}>
                                {' '.repeat(depth)}
                                {node.name}
                              </option>
                            ))}
                        </Select>
                        <Button type="submit" size="sm" loading={assign.isPending}>
                          Выдать
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setAssignFor(null)}>
                          Отмена
                        </Button>
                      </form>
                    ) : (
                      <button type="button" className="link text-[13px]" onClick={() => setAssignFor(u.id)}>
                        Выдать роль
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-[13px] text-steel">
                  {u.lastLoginAt ? formatMoment(u.lastLoginAt) : 'не входил'}
                </td>
                <td className="px-4 py-2.5 text-right">
                  {u.id !== me.id && (
                    <Button
                      size="sm"
                      variant={u.isActive ? 'ghost' : 'secondary'}
                      onClick={() => toggle.mutate(u)}
                    >
                      {u.isActive ? 'Отключить' : 'Включить'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewUserForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const people = useQuery({
    queryKey: ['org', 'employees', 'without-login'],
    queryFn: () =>
      api<{ items: { id: string; fullName: string; hasLogin: boolean; departmentName: string | null }[] }>(
        '/org/employees',
        {
          query: { pageSize: 200 },
        },
      ),
    enabled: open,
  });
  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/access/users', { method: 'POST', body }),
    onSuccess: () => {
      setOpen(false);
      onCreated();
    },
    onError: (err) =>
      err instanceof ApiError && setErrors(Object.keys(err.fields).length ? err.fields : { _: err.message }),
  });

  if (!open) {
    return (
      <Button className="mb-4" onClick={() => setOpen(true)}>
        Создать учётку
      </Button>
    );
  }
  return (
    <form
      className="panel mb-5 grid gap-4 px-5 py-5 sm:grid-cols-3"
      noValidate
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        create.mutate(Object.fromEntries(new FormData(e.currentTarget)));
      }}
    >
      <Field label="Сотрудник" error={errors.employeeId}>
        {(p) => (
          <Select {...p} name="employeeId">
            <option value="">Выберите сотрудника</option>
            {people.data?.items
              .filter((e) => !e.hasLogin)
              .map((e) => (
                <option key={e.id} value={e.id}>
                  {e.fullName}
                  {e.departmentName ? ` (${e.departmentName})` : ''}
                </option>
              ))}
          </Select>
        )}
      </Field>
      <Field label="Логин" error={errors.login} hint="Латиница, например i.ivanov">
        {(p) => <Input {...p} name="login" autoComplete="off" spellCheck={false} />}
      </Field>
      <Field
        label="Пароль"
        error={errors.password}
        hint="Не короче 8 символов, сотрудник сменит его в профиле"
      >
        {(p) => <Input {...p} name="password" type="text" autoComplete="off" />}
      </Field>
      {errors._ && (
        <p role="alert" className="text-[13px] text-danger sm:col-span-3">
          {errors._}
        </p>
      )}
      <div className="flex gap-2 sm:col-span-3">
        <Button type="submit" loading={create.isPending}>
          Создать учётку
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Отмена
        </Button>
      </div>
    </form>
  );
}

function RolesTab() {
  const qc = useQueryClient();
  const roles = useQuery({ queryKey: ['access', 'roles'], queryFn: () => api<Role[]>('/access/roles') });
  const save = useMutation({
    mutationFn: (v: { id: string; permissions: string[] }) =>
      api(`/access/roles/${v.id}/permissions`, { method: 'PUT', body: { permissions: v.permissions } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['access', 'roles'] }),
  });
  if (roles.isPending) return <Spinner />;
  if (roles.error) return <ErrorBox error={roles.error} />;
  const codes = Object.keys(PERMISSIONS) as (keyof typeof PERMISSIONS)[];

  return (
    <div className="panel overflow-x-auto">
      <table className="w-full min-w-[760px] text-left">
        <thead>
          <tr className="border-b border-line align-bottom text-[13px]">
            <th scope="col" className="px-4 py-3 font-medium text-steel">
              Право
            </th>
            {roles.data.map((r) => (
              <th key={r.id} scope="col" className="px-3 py-3 text-center font-medium">
                {r.name}
                {r.isImplicit && (
                  <span className="block text-[11.5px] font-normal text-steel">автоматически</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {codes.map((code) => (
            <tr key={code}>
              <th scope="row" className="px-4 py-2 font-normal">
                {PERMISSIONS[code]}
              </th>
              {roles.data.map((r) => {
                const on = r.permissions.includes(code);
                const locked = r.code === 'admin';
                return (
                  <td key={r.id} className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={locked || save.isPending}
                      aria-label={`${PERMISSIONS[code]} — ${r.name}`}
                      className="size-4 accent-[var(--color-graphite)]"
                      onChange={() =>
                        save.mutate({
                          id: r.id,
                          permissions: on
                            ? r.permissions.filter((p) => p !== code)
                            : [...r.permissions, code],
                        })
                      }
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="border-t border-line px-4 py-3 text-[13px] text-steel">
        Права администратора не редактируются. Изменения действуют сразу — перевходить не нужно.{' '}
        <Badge>Руководитель отдела</Badge> действует только на ветку своего отдела.
      </p>
    </div>
  );
}
